import React, { useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Loader2, Check, LayoutTemplate, Wand2, AlertTriangle } from 'lucide-react';
import { useLang, pick } from '@/lib/i18n';
import { api, errMsg, pollAiJob } from '@/lib/api';
import { SECTION_DEFS } from '@/lib/sectionDefs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

const TONES = [
  { v: 'professional', id: 'Profesional', en: 'Professional' },
  { v: 'friendly', id: 'Ramah & hangat', en: 'Friendly & warm' },
  { v: 'luxury', id: 'Premium / mewah', en: 'Premium / luxury' },
  { v: 'playful', id: 'Ceria & santai', en: 'Playful & casual' },
  { v: 'bold', id: 'Tegas & percaya diri', en: 'Bold & confident' },
];

export const AiGenerateDialog = ({ open, onOpenChange, site, companyName, hasContent, onApplied, landing = false }) => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ business_name: site?.navbar?.brand || site?.name || companyName || '', industry: '', tone: 'professional', description: '' });
  const [step, setStep] = useState('form'); // form | generating | preview | applying
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState('replace');

  const STEPS = lang === 'id' ? ['Menganalisis bisnis Anda', 'Menyusun struktur halaman', 'Menulis konten ID & EN', 'Memilih tema warna'] : ['Analyzing your business', 'Structuring pages', 'Writing ID & EN copy', 'Choosing color theme'];

  const generate = async (e) => {
    e.preventDefault();
    setStep('generating');
    setProgress(5);
    const timer = setInterval(() => setProgress((p) => (p < 90 ? p + 1.5 : p)), 700);
    try {
      const { data: job } = await api.post(`/sites/${site.id}/ai/generate-site`, { ...form, primary_lang: site.default_lang || 'id', landing });
      const data = await pollAiJob(site.id, job.job_id);
      if (!data || !Array.isArray(data.pages) || !data.pages.length) throw new Error(lang === 'id' ? 'Hasil AI tidak lengkap (tidak ada halaman). Coba lagi.' : 'AI result incomplete (no pages). Please retry.');
      data.pages = data.pages.map((p) => ({ ...p, sections: Array.isArray(p.sections) ? p.sections.filter((x) => x && typeof x === 'object') : [] }));
      setResult(data);
      setProgress(100);
      setStep('preview');
    } catch (err) {
      toast.error(err?.response ? errMsg(err) : err.message);
      setStep('form');
    } finally {
      clearInterval(timer);
    }
  };

  const apply = async () => {
    setStep('applying');
    try {
      const { data } = await api.post(`/sites/${site.id}/ai/apply-generated`, { data: result, mode });
      toast.success(lang === 'id' ? 'Website berhasil dibuat oleh AI' : 'Website generated by AI');
      onApplied(data);
      onOpenChange(false);
      setStep('form');
      setResult(null);
    } catch (err) {
      toast.error(errMsg(err));
      setStep('preview');
    }
  };

  const stepIdx = Math.min(STEPS.length - 1, Math.floor(progress / 25));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (step !== 'generating' && step !== 'applying') onOpenChange(o); }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="text-[hsl(var(--pillot-mint))]" size={20} />{t('generate_with_ai')}</DialogTitle>
          <DialogDescription>{lang === 'id' ? 'Jelaskan bisnis Anda, AI akan menyusun seluruh website lengkap dalam Bahasa Indonesia dan Inggris.' : 'Describe your business and AI will build a complete bilingual website.'}</DialogDescription>
        </DialogHeader>

        {step === 'form' && (
          <form onSubmit={generate} className="space-y-4" data-testid="ai-generate-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>{lang === 'id' ? 'Nama bisnis' : 'Business name'}</Label><Input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="rounded-xl" data-testid="ai-business-name-input" /></div>
              <div className="space-y-1.5"><Label>{t('industry')}</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder={lang === 'id' ? 'cth. Kedai kopi' : 'e.g. Coffee shop'} className="rounded-xl" data-testid="ai-industry-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>{lang === 'id' ? 'Nada bahasa' : 'Tone'}</Label>
              <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}><SelectTrigger className="rounded-xl" data-testid="ai-tone-select"><SelectValue /></SelectTrigger><SelectContent>{TONES.map((o) => <SelectItem key={o.v} value={o.v}>{lang === 'id' ? o.id : o.en}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>{lang === 'id' ? 'Deskripsi bisnis' : 'Business description'}</Label>
              <Textarea required minLength={10} rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={lang === 'id' ? 'Contoh: Kedai kopi specialty di Bandung dengan biji lokal dari petani Jawa Barat. Kami juga menyediakan kelas barista dan katering untuk acara kantor. Target pelanggan: pekerja kreatif usia 25-40.' : 'Example: A specialty coffee shop in Bandung sourcing local beans. We also run barista classes and office catering. Target: creative professionals 25-40.'} className="rounded-xl" data-testid="ai-description-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>{t('cancel')}</Button>
              <Button type="submit" className="rounded-xl" data-testid="ai-generate-submit-button"><Wand2 size={16} className="mr-1.5" />{lang === 'id' ? 'Buat Website' : 'Generate Website'}</Button>
            </DialogFooter>
          </form>
        )}

        {(step === 'generating' || step === 'applying') && (
          <div className="py-6 space-y-5" data-testid="ai-generating-state">
            <div className="flex items-center justify-center"><div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-secondary"><Sparkles className="text-[hsl(var(--pillot-mint))] animate-pulse" size={32} /><Loader2 className="absolute inset-0 h-20 w-20 animate-spin text-[hsl(var(--pillot-mint))]/40" strokeWidth={1} /></div></div>
            <Progress value={step === 'applying' ? 100 : progress} className="h-2" />
            <ul className="space-y-2 text-sm">
              {STEPS.map((s, i) => (
                <li key={s} className={`flex items-center gap-2 ${i < stepIdx || step === 'applying' ? 'text-emerald-700' : i === stepIdx ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                  {i < stepIdx || step === 'applying' ? <Check size={14} /> : i === stepIdx ? <Loader2 size={14} className="animate-spin" /> : <span className="h-3.5 w-3.5 rounded-full border border-border" />}{s}
                </li>
              ))}
            </ul>
            <p className="text-center text-xs text-muted-foreground">{step === 'applying' ? (lang === 'id' ? 'Menerapkan ke website...' : 'Applying to site...') : (lang === 'id' ? 'Biasanya 30-90 detik. Jangan tutup jendela ini.' : 'Usually 30-90 seconds. Keep this window open.')}</p>
          </div>
        )}

        {step === 'preview' && result && (
          <div className="space-y-4" data-testid="ai-preview-state">
            <div className="rounded-xl border border-border bg-muted/40 p-4">
              <div className="flex items-center justify-between">
                <div><div className="font-display font-semibold">{result.site_name}</div><div className="text-sm text-muted-foreground">{pick(result.tagline, lang)}</div></div>
                <div className="flex gap-1.5">{['primary', 'secondary'].map((k) => <span key={k} className="h-7 w-7 rounded-full border border-white shadow" style={{ background: result.theme?.[k] }} title={result.theme?.[k]} />)}</div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">Font: {result.theme?.font}</div>
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto scrollbar-thin pr-1">
              {(result.pages || []).map((p, i) => (
                <div key={i} className="rounded-xl border border-border bg-white p-3">
                  <div className="flex items-center gap-2 text-sm font-medium"><LayoutTemplate size={14} className="text-primary" />{pick(p.title, lang)} <span className="text-xs text-muted-foreground">/{p.is_home ? '' : p.slug}</span>{p.is_home && <Badge variant="secondary" className="rounded-full text-[10px]">Home</Badge>}</div>
                  <div className="mt-2 flex flex-wrap gap-1">{(p.sections || []).map((s, j) => <span key={j} className="rounded-md bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground">{pick(SECTION_DEFS[s.type]?.label, lang) || s.type}</span>)}</div>
                </div>
              ))}
            </div>
            {hasContent && (
              <div className="space-y-2">
                <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"><AlertTriangle size={14} className="mt-0.5 shrink-0" />{lang === 'id' ? 'Website ini sudah memiliki konten. Pilih cara penerapan:' : 'This site already has content. Choose how to apply:'}</div>
                <div className="grid grid-cols-2 gap-2">
                  {[{ v: 'replace', id: 'Ganti semua halaman', en: 'Replace all pages' }, { v: 'append', id: 'Tambahkan sebagai halaman baru', en: 'Append as new pages' }].map((o) => (
                    <button key={o.v} type="button" onClick={() => setMode(o.v)} className={`rounded-xl border p-3 text-left text-sm transition-colors ${mode === o.v ? 'border-primary bg-secondary/60' : 'border-border hover:bg-muted'}`} data-testid={`ai-apply-mode-${o.v}`}>{lang === 'id' ? o.id : o.en}</button>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => setStep('form')} data-testid="ai-regenerate-button">{lang === 'id' ? 'Ubah & buat ulang' : 'Edit & regenerate'}</Button>
              <Button type="button" className="rounded-xl" onClick={apply} data-testid="ai-apply-button"><Check size={16} className="mr-1.5" />{lang === 'id' ? 'Terapkan ke Website' : 'Apply to Website'}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
